import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiFetch } from '../api/api';
import { DataForm } from '../components/forms';
import { DataTable } from '../components/tables';
import './WellCasingTally.css';

const WellCasingTally = () => {
    // Core state
    const [wells, setWells] = useState([]);
    const [selectedWellId, setSelectedWellId] = useState('');
    const [selectedWell, setSelectedWell] = useState(null);
    
    // Data state
    const [casingTallyData, setCasingTallyData] = useState([]);
    
    // Column configurations
    const [casingTallyColumns, setCasingTallyColumns] = useState([]);
    
    // UI state
    const [isLoading, setIsLoading] = useState(false);
    const [toast, setToast] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [showDataForm, setShowDataForm] = useState(false);
    const [dataFormConfig, setDataFormConfig] = useState({});
    const [editingRecord, setEditingRecord] = useState(null);
    
    // Collapsible sections state
    const [collapsedSections, setCollapsedSections] = useState({
        overview: false,
        tally: false,
        summary: false
    });

    // Statistics state
    const [statistics, setStatistics] = useState({
        totalJoints: 0,
        totalLength: 0,
        averageJointLength: 0,
        totalWeight: 0,
        uniqueSizes: 0
    });

    // Quick actions state
    const [quickActions, setQuickActions] = useState({
        showQuickAdd: false,
        selectedQuickAction: null
    });

    // Export state
    const [exporting, setExporting] = useState(false);

    const role = localStorage.getItem('role') || 'admin';

    // Load initial data
    useEffect(() => {
        loadWells();
        loadColumnConfigurations();
    }, []);

    // Load tally data when well is selected
    useEffect(() => {
        if (selectedWellId) {
            loadCasingTally();
            loadWellDetails();
        } else {
            setCasingTallyData([]);
            setSelectedWell(null);
        }
    }, [selectedWellId]);

    // Calculate statistics when data changes
    useEffect(() => {
        calculateStatistics();
    }, [casingTallyData]);

    const loadWells = async () => {
        try {
            const response = await apiFetch('/api/wells');
            setWells(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load wells:', error);
            showToast('❌ Failed to load wells');
        }
    };

    const loadWellDetails = async () => {
        try {
            const response = await apiFetch(`/api/wells/${selectedWellId}`);
            setSelectedWell(response);
        } catch (error) {
            console.error('Failed to load well details:', error);
        }
    };

    const loadColumnConfigurations = async () => {
        try {
            const response = await apiFetch('/api/config/columns');
            const allConfigs = Array.isArray(response) ? response : [];
            
            const casingTallyConfig = allConfigs.find(config => config.tableName === 'CasingTallySheet');
            
            if (casingTallyConfig) setCasingTallyColumns(casingTallyConfig.columns.sort((a, b) => a.order - b.order));
        } catch (error) {
            console.error('Failed to load column configurations:', error);
            showToast('❌ Failed to load configurations');
        }
    };

    const loadCasingTally = async () => {
        try {
            setIsLoading(true);
            const response = await apiFetch(`/api/db/CasingTallySheet?WellID=${selectedWellId}`);
            setCasingTallyData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error('Failed to load casing tally:', error);
            setCasingTallyData([]);
        } finally {
            setIsLoading(false);
        }
    };

    const calculateStatistics = () => {
        const stats = {
            totalJoints: casingTallyData.length,
            totalLength: casingTallyData.reduce((sum, joint) => sum + (parseFloat(joint.Length) || 0), 0),
            averageJointLength: casingTallyData.length > 0 
                ? casingTallyData.reduce((sum, joint) => sum + (parseFloat(joint.Length) || 0), 0) / casingTallyData.length 
                : 0,
            totalWeight: casingTallyData.reduce((sum, joint) => sum + (parseFloat(joint.Weight) || 0), 0),
            uniqueSizes: new Set(casingTallyData.map(joint => joint.CasingSize).filter(Boolean)).size
        };
        setStatistics(stats);
    };

    const handleWellChange = (e) => {
        setSelectedWellId(e.target.value);
        setCasingTallyData([]);
    };

    const toggleSection = (sectionId) => {
        setCollapsedSections(prev => ({
            ...prev,
            [sectionId]: !prev[sectionId]
        }));
    };

    const showToast = (message) => {
        setToast(message);
        setTimeout(() => setToast(''), 3000);
    };

    const handleDataChange = async () => {
        // Refresh data after any changes
        if (selectedWellId) {
            await loadCasingTally();
        }
    };

    const renderWellSelector = () => (
        <div className="well-selector">
            <div className="selector-row">
                <div className="selector-group">
                    <label htmlFor="well-select">Well:</label>
                    <select
                        id="well-select"
                        value={selectedWellId}
                        onChange={handleWellChange}
                        className="form-select"
                    >
                        <option value="">Select a well...</option>
                        {wells.map(well => (
                            <option key={well.WellID} value={well.WellID}>
                                {well.WellName} {well.Field ? `- ${well.Field}` : ''}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            
            {selectedWell && (
                <div className="well-info">
                    <div className="well-details">
                        <strong>{selectedWell.WellName}</strong>
                        {selectedWell.Field && <span> • Field: {selectedWell.Field}</span>}
                        {selectedWell.TotalDepth && <span> • TD: {selectedWell.TotalDepth}m</span>}
                        {selectedWell.Operator && <span> • Operator: {selectedWell.Operator}</span>}
                    </div>
                </div>
            )}
        </div>
    );

    const renderQuickActions = () => (
        <div className="quick-actions">
            <button
                className="quick-action-btn small"
                onClick={() => handleQuickAction('addTally')}
                title="Add Tally Joint"
            >
                📋 Add Joint
            </button>
            <button
                className="quick-action-btn small"
                onClick={() => handleQuickAction('exportData')}
                disabled={exporting}
                title="Export Data"
            >
                {exporting ? '⏳ Exporting...' : '📤 Export'}
            </button>
        </div>
    );

    const renderTabNavigation = () => (
        <div className="tab-navigation">
            <button
                className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
                onClick={() => setActiveTab('overview')}
            >
                📊 Overview
            </button>
            <button
                className={`tab ${activeTab === 'tally' ? 'active' : ''}`}
                onClick={() => setActiveTab('tally')}
            >
                📋 Tally Details
            </button>
            <button
                className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
                onClick={() => setActiveTab('summary')}
            >
                📈 Summary
            </button>
        </div>
    );

    const renderDataSummary = () => (
        <div className="data-summary">
            <div className="summary-section">
                <h4>📊 Tally Summary</h4>
                <div className="summary-grid">
                    <div className="summary-item">
                        <span className="summary-label">Total Joints:</span>
                        <span className="summary-value">{statistics.totalJoints}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Total Length:</span>
                        <span className="summary-value">{statistics.totalLength.toFixed(2)}m</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Avg Joint Length:</span>
                        <span className="summary-value">{statistics.averageJointLength.toFixed(2)}m</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Total Weight:</span>
                        <span className="summary-value">{statistics.totalWeight.toFixed(2)}kg</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Unique Sizes:</span>
                        <span className="summary-value">{statistics.uniqueSizes}</span>
                    </div>
                </div>
            </div>
        </div>
    );

    const CollapsibleSection = ({ title, children, sectionId, isEmpty = false, onAdd = null }) => {
        const isCollapsed = collapsedSections[sectionId];
        
        return (
            <div className="collapsible-section">
                <div className="section-header" onClick={() => toggleSection(sectionId)}>
                    <div className="section-title">
                        <span className={`collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}>
                            {isCollapsed ? '▶' : '▼'}
                        </span>
                        {title}
                    </div>
                    {onAdd && (role === 'admin' || role === 'read-write') && (
                        <button
                            className="add-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                onAdd();
                            }}
                        >
                            ➕ Add
                        </button>
                    )}
                </div>
                {!isCollapsed && (
                    <div className="section-content">
                        {isEmpty && (role === 'admin' || role === 'read-write') ? (
                            <div className="empty-section">
                                <p>No data available</p>
                                {onAdd && (
                                    <button className="add-data-btn" onClick={onAdd}>
                                        ➕ Add Data
                                    </button>
                                )}
                            </div>
                        ) : (
                            children
                        )}
                    </div>
                )}
            </div>
        );
    };

    const renderCasingTallyTable = () => {
        const tallyWithActions = casingTallyData.map(joint => ({
            ...joint,
            actions: (
                <div className="action-buttons">
                    {(role === 'admin' || role === 'read-write') && (
                        <>
                            <button
                                className="action-btn edit-btn"
                                onClick={() => handleEditRecord('CasingTallySheet', joint)}
                                title="Edit Joint"
                            >
                                ✏️
                            </button>
                            <button
                                className="action-btn delete-btn"
                                onClick={() => handleDeleteRecord('CasingTallySheet', joint)}
                                title="Delete Joint"
                            >
                                🗑️
                            </button>
                        </>
                    )}
                </div>
            )
        }));

        return (
            <DataTable
                data={tallyWithActions}
                columns={[
                    ...casingTallyColumns,
                    {
                        name: 'actions',
                        label: 'Actions',
                        type: 'custom',
                        order: 999
                    }
                ]}
                role={role}
                tableName="CasingTallySheet"
                title="Casing Tally Sheet"
                onDataChange={handleDataChange}
                itemsPerPage={20}
            />
        );
    };

    const renderTallyTable = () => {
        if (casingTallyData.length === 0) {
            return (
                <div className="no-selection">
                    <p>No tally data available for this well</p>
                </div>
            );
        }

        return renderCasingTallyTable();
    };

    // Handlers
    const handleAddRecord = (tableName, defaultData = {}) => {
        let config = {};
        let data = { ...defaultData };
        
        if (tableName === 'CasingTallySheet') {
            data.WellID = selectedWellId;
            config = {
                formType: 'single',
                tableName: 'CasingTallySheet',
                data,
                title: 'Add New Tally Joint'
            };
        }
        
        setDataFormConfig(config);
        setEditingRecord(null);
        setShowDataForm(true);
    };

    const handleEditRecord = (tableName, record) => {
        let config = {};
        
        if (tableName === 'CasingTallySheet') {
            config = {
                formType: 'single',
                tableName: 'CasingTallySheet',
                data: { ...record },
                title: 'Edit Tally Joint'
            };
        }
        
        setDataFormConfig(config);
        setEditingRecord(record);
        setShowDataForm(true);
    };

    const handleCancelForm = () => {
        setShowDataForm(false);
        setEditingRecord(null);
        setDataFormConfig({});
    };

    const handleDataFormSave = useCallback(async (result) => {
        try {
            console.log('✅ Data save completed:', result);
            showToast('✅ Data saved successfully');
            
            setShowDataForm(false);
            setEditingRecord(null);
            setDataFormConfig({});
            
            // Refresh data
            await handleDataChange();
        } catch (error) {
            console.error('❌ Failed to save data:', error);
            showToast('❌ Failed to save data: ' + error.message);
        }
    }, []);

    const handleDeleteRecord = async (tableName, record) => {
        if (!window.confirm('Are you sure you want to delete this record?')) {
            return;
        }

        try {
            // Determine the primary key field name
            let primaryKey = 'ID';
            if (tableName === 'CasingTallySheet') primaryKey = 'TallyID';

            await apiFetch(`/api/db/${tableName}/${record[primaryKey]}`, {
                method: 'DELETE'
            });

            showToast('✅ Record deleted successfully');
            await handleDataChange();
        } catch (error) {
            console.error('Failed to delete record:', error);
            showToast('❌ Failed to delete record');
        }
    };

    const handleQuickAction = (action) => {
        switch (action) {
            case 'addTally':
                handleAddRecord('CasingTallySheet');
                break;
            case 'exportData':
                handleExportData();
                break;
            default:
                break;
        }
        setQuickActions({ showQuickAdd: false, selectedQuickAction: null });
    };

    const handleExportData = async () => {
        if (!selectedWellId) {
            showToast('❌ Please select a well first');
            return;
        }

        setExporting(true);
        try {
            const exportData = {
                well: selectedWell,
                casingTally: casingTallyData,
                statistics: statistics,
                exportDate: new Date().toISOString()
            };

            const dataStr = JSON.stringify(exportData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `casing_tally_${selectedWell?.WellName}_${new Date().toISOString().split('T')[0]}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            showToast('✅ Data exported successfully');
        } catch (error) {
            console.error('Export failed:', error);
            showToast('❌ Export failed');
        }
        setExporting(false);
    };

    const renderLeftPanel = () => (
        <div className="bha-list-panel">
            {/* Tally Summary */}
            <div className="section-header">
                <h3>Tally Summary</h3>
            </div>
            <div>
                <div className="tally-summary">
                    <div className="summary-item">
                        <span className="summary-label">Total Joints:</span>
                        <span className="summary-value">{casingTallyData.length}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Total Length:</span>
                        <span className="summary-value">
                            {casingTallyData.reduce((sum, joint) => sum + (parseFloat(joint.Length) || 0), 0).toFixed(2)}m
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Total Weight:</span>
                        <span className="summary-value">
                            {casingTallyData.reduce((sum, joint) => sum + (parseFloat(joint.Weight) || 0), 0).toFixed(2)}kg
                        </span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Unique Sizes:</span>
                        <span className="summary-value">
                            {new Set(casingTallyData.map(joint => joint.CasingSize).filter(Boolean)).size}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );

    const renderRightPanel = () => (
        <div className="bha-details-panel">
            {showDataForm ? (
                <DataForm
                    data={dataFormConfig.data}
                    tableName={dataFormConfig.tableName}
                    columnsPerRow={2}
                    onSave={handleDataFormSave}
                    onCancel={handleCancelForm}
                    title={dataFormConfig.title}
                />
            ) : (
                <div className="placeholder-container">
                    <div className="placeholder-content">
                        <div className="placeholder-icon">📋</div>
                        <p>Select a casing run to view its tally or add new data.</p>
                    </div>
                </div>
            )}
        </div>
    );

    if (isLoading) {
        return (
            <div className="casing-tally-container loading">
                <div className="loading-spinner">Loading...</div>
            </div>
        );
    }

    return (
        <div className="wellbha-container fullscreen">
            <div className="wellbha-header">
                <div className="header-main">
                    <h1>📋 Casing Tally Management</h1>
                    {renderWellSelector()}
                </div>
                {renderQuickActions()}
            </div>
            <div className="wellbha-content-grid">
                {renderLeftPanel()}
                {renderRightPanel()}
            </div>
            {renderTabNavigation()}
            
            {activeTab === 'overview' && (
                <div className="overview-content">
                    {renderDataSummary()}
                    
                    <CollapsibleSection
                        title="Casing Tally Summary"
                        sectionId="tally"
                        isEmpty={casingTallyData.length === 0}
                        onAdd={() => handleAddRecord('CasingTallySheet')}
                    >
                        <div className="tally-summary-grid">
                            {casingTallyData.map(joint => (
                                <div key={joint.TallyID} className="tally-card">
                                    <div className="tally-header">
                                        <h4>Joint #{joint.JointNumber || joint.TallyID}</h4>
                                        <div className="tally-actions">
                                            {(role === 'admin' || role === 'read-write') && (
                                                <>
                                                    <button
                                                        className="edit-btn"
                                                        onClick={() => handleEditRecord('CasingTallySheet', joint)}
                                                    >
                                                        ✏️
                                                    </button>
                                                    <button
                                                        className="delete-btn"
                                                        onClick={() => handleDeleteRecord('CasingTallySheet', joint)}
                                                    >
                                                        🗑️
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    <div className="tally-details">
                                        <span><strong>Size:</strong> {joint.CasingSize}</span>
                                        <span><strong>Length:</strong> {joint.Length}m</span>
                                        <span><strong>Weight:</strong> {joint.Weight}kg</span>
                                        {joint.Grade && <span><strong>Grade:</strong> {joint.Grade}</span>}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </CollapsibleSection>
                </div>
            )}

            {activeTab === 'tally' && (
                <CollapsibleSection
                    title="Casing Tally Details"
                    sectionId="tally"
                    isEmpty={casingTallyData.length === 0}
                    onAdd={() => handleAddRecord('CasingTallySheet')}
                >
                    {renderTallyTable()}
                </CollapsibleSection>
            )}

            {activeTab === 'summary' && (
                <CollapsibleSection
                    title="Tally Summary by Size"
                    sectionId="summary"
                    isEmpty={casingTallyData.length === 0}
                    onAdd={() => handleAddRecord('CasingTallySheet')}
                >
                    <div className="summary-table">
                        {(() => {
                            const sizeGroups = {};
                            casingTallyData.forEach(joint => {
                                const size = joint.CasingSize || 'Unknown';
                                if (!sizeGroups[size]) {
                                    sizeGroups[size] = [];
                                }
                                sizeGroups[size].push(joint);
                            });

                            return Object.entries(sizeGroups).map(([size, joints]) => {
                                const totalLength = joints.reduce((sum, joint) => sum + (parseFloat(joint.Length) || 0), 0);
                                const totalWeight = joints.reduce((sum, joint) => sum + (parseFloat(joint.Weight) || 0), 0);
                                
                                return (
                                    <div key={size} className="summary-card">
                                        <div className="summary-header">
                                            <h4>{size}</h4>
                                        </div>
                                        <div className="summary-stats">
                                            <div className="stat-item">
                                                <span className="stat-label">Joints:</span>
                                                <span className="stat-value">{joints.length}</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Length:</span>
                                                <span className="stat-value">{totalLength.toFixed(2)}m</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Weight:</span>
                                                <span className="stat-value">{totalWeight.toFixed(2)}kg</span>
                                            </div>
                                            <div className="stat-item">
                                                <span className="stat-label">Avg Length:</span>
                                                <span className="stat-value">{(totalLength / joints.length).toFixed(2)}m</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            });
                        })()}
                    </div>
                </CollapsibleSection>
            )}

            {/* Toast Message */}
            {toast && (
                <div className="toast-message">
                    {toast}
                </div>
            )}
        </div>
    );
};

export default WellCasingTally; 